import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ComboBox from "../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000CheckBox from "../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import { useU3129Context } from "../../U3129Context";
import { useU3129FindProjectHandlers } from "../../U3129UseFindProjectSubprojectHandlers";
import {
  newHonorariumClientRowId,
  type U3129EquipmentItem,
  type U3129EquipmentTypeItem,
  type U3129FindProjectItem,
  type U3129Timesheet,
  u3129EquipmentTypeAmount,
  u3129IsEquipmentTypeActive,
} from "../../U3129Service";
import { NS } from "../../U3129";
import { U3129LineProjectSubprojectRowCells } from "./U3129LineProjectSubprojectRowCells";
import { u3129FormatLineDateDisplay } from "../../tools/U3129FormatLineDate";
import { U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from "../../tools/u3129ReadonlyTableCellClass";
import {
  U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS,
  U3129_GRID_COMPACT_ROW_CELL_PY,
  U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS,
  U3129_GRID_COMPACT_TABLE_CONTROL_CLASS,
} from "../../tools/u3129TimesheetGridCompactStyles";
import { formatCurrency } from "../../../u2000Core/tools/U2000CurrencyUtils";
import { FaCheck, FaPlus, FaTimesCircle, FaTrash } from "react-icons/fa";

const U3129_EQUIPMENT_DATE_COL_CLASS = "w-[9rem] min-w-[9rem] max-w-[9rem]";
const U3129_EQUIPMENT_PROJECT_COL_CLASS = "w-[8%] min-w-48";
const U3129_EQUIPMENT_CUSTOMER_COL_CLASS = "w-[22%] min-w-[16rem]";
const U3129_EQUIPMENT_SUBPROJECT_COL_CLASS = "w-[8%] min-w-40";
const U3129_EQUIPMENT_TYPE_COL_CLASS = "min-w-48";
const U3129_EQUIPMENT_UNIT_TEXT_COL_CLASS = "min-w-32";
const U3129_EQUIPMENT_COST_COL_CLASS = "w-24 min-w-28";
const U3129_EQUIPMENT_QTY_COL_CLASS = "w-20 min-w-26";
const U3129_EQUIPMENT_AMOUNT_COL_CLASS = "w-36 min-w-36";
const U3129_EQUIPMENT_REMARK_COL_CLASS = "min-w-40";
const U3129_EQUIPMENT_TOTAL_LABEL_COL_CLASS = "min-w-5xl";

function equipmentTypeLabel(item: U3129EquipmentTypeItem): string {
  return (item.description?.trim() || item.code?.trim() || item.id) ?? item.id;
}

const U3129_READONLY_NUM_BOX_CLASS = `${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`;

function u3129FormatEquipmentCurrencyCell(value: unknown): string {
  const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return formatCurrency(n, { decimalDigits: 2 });
}

function u3129EquipmentTypeDisplay(equipmentTypeId: string | undefined, options: U3129EquipmentTypeItem[]): string {
  if (!equipmentTypeId?.trim()) return "—";
  const item = options.find((d) => d.id === equipmentTypeId);
  return item ? equipmentTypeLabel(item) : equipmentTypeId;
}

/** Montant ligne = coût unitaire × quantité (quantité entière ; arrondi monétaire 2 décimales). */
function computeEquipmentLineAmount(equipmentCost: unknown, quantity: unknown): number {
  const c = typeof equipmentCost === "number" && Number.isFinite(equipmentCost) ? equipmentCost : 0;
  let q = 0;
  if (typeof quantity === "number" && Number.isFinite(quantity) && Number.isInteger(quantity)) {
    q = quantity;
  }
  return Math.round(c * q * 100) / 100;
}

function emptyEquipmentRow(): U3129EquipmentItem {
  return {
    clientRowId: newHonorariumClientRowId(),
    transactionDate: null,
    projectId: "",
    subprojectId: "",
    equipmentTypeId: "",
    billingUnit: "",
    equipmentCost: 0,
    quantity: undefined,
    amount: 0,
    remark: "",
  };
}

interface U3129EquipmentTabProps {
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
}

interface EquipmentRowEditorProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3129FindProjectHandlers>;
  equipmentTypeOptions: U3129EquipmentTypeItem[];
  readonly: boolean;
  checkboxDisabled?: boolean;
  showSubProjectColumn: boolean;
  rowIncludeSubProject: boolean;
  getProjectId: () => string | undefined | null;
}

function EquipmentRowEditor({
  index,
  formStateHandlers,
  findProjectHandlers,
  equipmentTypeOptions,
  readonly,
  checkboxDisabled,
  showSubProjectColumn,
  rowIncludeSubProject,
  getProjectId,
}: EquipmentRowEditorProps) {
  const { t, i18n } = useTranslation();
  const baseField = `equipment.${index}`;
  const prevEquipmentTypeIdRef = useRef<string | null>(null);

  const data = formStateHandlers.getData();
  const row = data?.equipment?.[index];
  const equipmentTypeId = row?.equipmentTypeId?.trim() ?? "";

  useEffect(() => {
    if (readonly) return;
    if (prevEquipmentTypeIdRef.current === null) {
      prevEquipmentTypeIdRef.current = equipmentTypeId;
      return;
    }
    if (equipmentTypeId === prevEquipmentTypeIdRef.current) return;
    prevEquipmentTypeIdRef.current = equipmentTypeId;
    if (!equipmentTypeId) return;
    const meta = equipmentTypeOptions.find((e) => e.id === equipmentTypeId);
    if (!meta) return;
    const currentData = formStateHandlers.getData();
    const list = currentData?.equipment ?? [];
    const currentRow = list[index];
    if (!currentRow) return;
    const amt = u3129EquipmentTypeAmount(meta);
    const lineAmount = computeEquipmentLineAmount(amt, currentRow.quantity);
    const updated = list.map((r, i) =>
      i === index
        ? {
            ...r,
            billingUnit: meta.billingUnit?.trim() ?? r.billingUnit ?? "",
            equipmentCost: amt,
            amount: lineAmount,
          }
        : r,
    );
    formStateHandlers.setData({ ...currentData, equipment: updated } as U3129Timesheet);
  }, [readonly, equipmentTypeId, equipmentTypeOptions, formStateHandlers, index]);

  useEffect(() => {
    if (readonly) return;
    const currentData = formStateHandlers.getData();
    const list = currentData?.equipment ?? [];
    const currentRow = list[index];
    if (!currentRow) return;
    const nextAmount = computeEquipmentLineAmount(currentRow.equipmentCost, currentRow.quantity);
    const prevAmount = typeof currentRow.amount === "number" && Number.isFinite(currentRow.amount) ? currentRow.amount : 0;
    if (Math.round(prevAmount * 100) === Math.round(nextAmount * 100)) return;
    const updated = list.map((r, i) => (i === index ? { ...r, amount: nextAmount } : r));
    formStateHandlers.setData({ ...currentData, equipment: updated } as U3129Timesheet);
  }, [readonly, index, formStateHandlers, row?.quantity, row?.equipmentCost]);

  if (readonly) {
    const rowRead = formStateHandlers.getData()?.equipment?.[index];
    return (
      <tr className="border-b border-gray-100 hover:bg-gray-50/80">
        <td className={U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS}>
          <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_DATE_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{u3129FormatLineDateDisplay(rowRead?.transactionDate, i18n.language)}</div>
        </td>
        <U3129LineProjectSubprojectRowCells
          baseField={baseField}
          arrayField="equipment"
          index={index}
          formStateHandlers={formStateHandlers}
          findProjectHandlers={findProjectHandlers}
          getProjectId={getProjectId}
          readonly={readonly}
          rowIncludeSubProject={rowIncludeSubProject}
          showSubProjectColumn={showSubProjectColumn}
          clearTaskFieldsOnProjectChange={false}
          compactTableControls
          ns={NS}
        />
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_TYPE_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>
            {u3129EquipmentTypeDisplay(rowRead?.equipmentTypeId, equipmentTypeOptions)}
          </div>
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_UNIT_TEXT_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{rowRead?.billingUnit?.trim() || "—"}</div>
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_COST_COL_CLASS} align-top text-right`}>
          <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatEquipmentCurrencyCell(rowRead?.equipmentCost)}</div>
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_QTY_COL_CLASS} align-top text-right`}>
          <div className={U3129_READONLY_NUM_BOX_CLASS}>
            {rowRead?.quantity != null && typeof rowRead.quantity === "number" && Number.isFinite(rowRead.quantity) && Number.isInteger(rowRead.quantity)
              ? String(rowRead.quantity)
              : ""}
          </div>
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_AMOUNT_COL_CLASS} align-top text-right`}>
          <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatEquipmentCurrencyCell(rowRead?.amount)}</div>
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_REMARK_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{rowRead?.remark?.trim() || "—"}</div>
        </td>
      </tr>
    );
  }

  return (
      <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className={U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS}>
        <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_DATE_COL_CLASS}`}>
        <U2000InputCalendarCustom
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.transactionDate`}
          label=""
          dateFormat="yyyy-MM-dd"
          compactGrid
        />
      </td>
      <U3129LineProjectSubprojectRowCells
        baseField={baseField}
        arrayField="equipment"
        index={index}
        formStateHandlers={formStateHandlers}
        findProjectHandlers={findProjectHandlers}
        getProjectId={getProjectId}
        readonly={readonly}
        rowIncludeSubProject={rowIncludeSubProject}
        showSubProjectColumn={showSubProjectColumn}
        clearTaskFieldsOnProjectChange={false}
        compactTableControls
        ns={NS}
      />
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_TYPE_COL_CLASS}`}>
        <U2000ComboBox<U3129EquipmentTypeItem>
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.equipmentTypeId`}
          label=""
          data={equipmentTypeOptions}
          keyField="id"
          descField="description"
          formatLabel={(item) => equipmentTypeLabel(item)}
          findItemByValue={(value, list) => list.find((d) => d.id === value) ?? null}
          getValue={(item) => item.id}
          placeholder={t("placeholderSelectEquipmentType", { ns: NS, defaultValue: "Type équipement" })}
          compactGrid
        />
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_UNIT_TEXT_COL_CLASS}`}>
        <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{row?.billingUnit?.trim() || "—"}</div>
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_COST_COL_CLASS} align-top text-right`}>
        <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatEquipmentCurrencyCell(row?.equipmentCost)}</div>
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_QTY_COL_CLASS} align-top text-right`}>
        <div className={U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS}>
          <U2000InputDecimal formStateHandlers={formStateHandlers} fieldName={`${baseField}.quantity`} label="" decimalDigits={0} textAlign="right" />
        </div>
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EQUIPMENT_AMOUNT_COL_CLASS} align-top text-right`}>
        <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatEquipmentCurrencyCell(row?.amount)}</div>
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EQUIPMENT_REMARK_COL_CLASS}`}>
        <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${baseField}.remark`} label="" />
      </td>
    </tr>
  );
}

const U3129EquipmentTab: React.FC<U3129EquipmentTabProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const { initLU } = useU3129Context();
  const showSubProjectColumn = useMemo(() => initLU.personalization?.admIncludeSubProject === "true", [initLU.personalization?.admIncludeSubProject]);
  const equipmentTableColCount = showSubProjectColumn ? 11 : 10;
  const equipmentFooterFirstColSpan = showSubProjectColumn ? 9 : 8;
  const [deletePendingIndices, setDeletePendingIndices] = useState<number[] | null>(null);
  const confirmYesButtonRef = useRef<HTMLButtonElement>(null);
  const findProjectHandlers = useU3129FindProjectHandlers();

  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || (data != null && data.status !== "Open");

  const equipmentTypeOptions = useMemo(() => (initLU.equipmentTypes ?? []).filter((x) => u3129IsEquipmentTypeActive(x)), [initLU.equipmentTypes]);

  useEffect(() => {
    const current = formStateHandlers.getData();
    const list = current?.equipment ?? [];
    if (!list.length) return;
    let changed = false;
    const next = list.map((r) => {
      if (r.clientRowId?.trim()) return r;
      changed = true;
      return { ...r, clientRowId: newHonorariumClientRowId() };
    });
    if (changed) {
      formStateHandlers.setData({ ...current, equipment: next } as U3129Timesheet);
    }
  }, [data?.equipment, formStateHandlers]);

  const rows = useMemo(() => data?.equipment ?? [], [data?.equipment]);

  const hasCheckedRow = useMemo(() => rows.some((row) => (row as unknown as Record<string, unknown>).checked === true), [rows]);

  useEffect(() => {
    if (deletePendingIndices != null && deletePendingIndices.length > 0) {
      const timer = setTimeout(() => confirmYesButtonRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [deletePendingIndices]);

  const handleAddRow = useCallback(() => {
    const current = formStateHandlers.getData();
    const list = current?.equipment ?? [];
    formStateHandlers.setData({
      ...current,
      equipment: [...list, emptyEquipmentRow()],
    } as U3129Timesheet);
  }, [formStateHandlers]);

  const handleDeleteCheckedRow = useCallback(() => {
    const list = formStateHandlers.getData()?.equipment ?? [];
    const indices = list.map((r, i) => ((r as unknown as Record<string, unknown>).checked === true ? i : -1)).filter((i) => i >= 0);
    if (indices.length > 0) setDeletePendingIndices(indices);
  }, [formStateHandlers]);

  const confirmDeleteRow = useCallback(() => {
    if (deletePendingIndices == null || deletePendingIndices.length === 0) return;
    const toRemove = new Set(deletePendingIndices);
    const current = formStateHandlers.getData();
    const list = current?.equipment ?? [];
    const next = list.filter((_, i) => !toRemove.has(i));
    const nextData = {
      ...current,
      equipment: next,
    } as U3129Timesheet;
    formStateHandlers.setData(nextData);
    const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
    formStateHandlers.setErrors(validateForm ? validateForm(nextData) : {});
    setDeletePendingIndices(null);
  }, [deletePendingIndices, formStateHandlers]);

  const getProjectIdForRow = useCallback(
    (rowIndex: number) => {
      const r = formStateHandlers.getData()?.equipment?.[rowIndex];
      const proj = r && (r as unknown as Record<string, unknown>).project;
      const pid = proj && typeof proj === "object" && "id" in proj ? String((proj as { id?: string }).id ?? "") : "";
      return r?.projectId?.trim() || pid || undefined;
    },
    [formStateHandlers],
  );

  const getRowIncludeSubProject = useCallback(
    (rowIndex: number): boolean => {
      const r = formStateHandlers.getData()?.equipment?.[rowIndex];
      const project = (r as unknown as Record<string, unknown>)?.project as U3129FindProjectItem | undefined;
      const v = project?.includeSubProjet;
      return v === true || (typeof v === "string" && v === "true");
    },
    [formStateHandlers],
  );

  const totalAmount = useMemo(
    () =>
      rows.reduce((sum, row) => {
        const amount = row?.amount;
        return typeof amount === "number" && Number.isFinite(amount) ? sum + amount : sum;
      }, 0),
    [rows],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-muted/10 pt-4 px-4 pb-2">
      <div className="relative flex flex-col gap-4">
        {deletePendingIndices != null && deletePendingIndices.length > 0 && (
          <div
            className="absolute inset-0 z-100 flex items-center justify-center rounded-lg bg-black/30 backdrop-blur-[1px]"
            aria-modal="true"
            role="alertdialog"
            aria-labelledby="confirm-delete-equipment-title"
          >
            <div className="relative z-101 mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-lg">
              <h3 id="confirm-delete-equipment-title" className="text-lg font-semibold text-gray-900">
                {deletePendingIndices.length === 1
                  ? t("msgConfirmDeleteEquipmentRow", { ns: NS, defaultValue: "Supprimer cette ligne ?" })
                  : t("msgConfirmDeleteEquipmentRowsBulk", {
                      ns: NS,
                      count: deletePendingIndices.length,
                      defaultValue: "Supprimer {{count}} lignes d’équipement ?",
                    })}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {deletePendingIndices.length === 1
                  ? t("msgConfirmDeleteEquipmentRowDetail", { ns: NS, defaultValue: "Voulez-vous supprimer cette entrée de la liste d’équipement ?" })
                  : t("msgConfirmDeleteEquipmentRowsBulkDetail", {
                      ns: NS,
                      count: deletePendingIndices.length,
                      defaultValue: "Les {{count}} lignes cochées seront retirées de la liste d’équipement.",
                    })}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeletePendingIndices(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaTimesCircle className="h-4 w-4" />
                  {t("dlgLblNo", { ns: "common", defaultValue: "Non" })}
                </button>
                <button
                  ref={confirmYesButtonRef}
                  type="button"
                  onClick={confirmDeleteRow}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  <FaCheck className="h-4 w-4" />
                  {t("dlgLblYes", { ns: "common", defaultValue: "Oui" })}
                </button>
              </div>
            </div>
          </div>
        )}

        {!readonly && (
          <div className="flex justify-start gap-2">
            <U2000ActionBtn label={t("btnAdd", { ns: NS })} icon={FaPlus} handleClick={handleAddRow} />
            <U2000ActionBtn
              label={t("btnDelete", { ns: NS })}
              icon={FaTrash}
              handleClick={handleDeleteCheckedRow}
              disabled={!hasCheckedRow}
              className="bg-red-600! hover:bg-red-700! focus:ring-red-500! active:bg-red-800! disabled:bg-gray-400!"
            />
          </div>
        )}

        <div className={`min-w-0 overflow-x-auto rounded-lg border border-gray-200 ${U3129_GRID_COMPACT_TABLE_CONTROL_CLASS}`}>
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-muted border-b border-gray-200 z-10">
              <tr>
                <th className="w-10 pl-2 pr-1 py-2.5" />
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_DATE_COL_CLASS}`}>{t("lblTransactionDate", { ns: NS, defaultValue: "Date" })}</th>
                <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_PROJECT_COL_CLASS}`}>{t("lblProject", { ns: NS })}</th>
                <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_CUSTOMER_COL_CLASS}`}>{t("lblCustomer", { ns: NS })}</th>
                {showSubProjectColumn ? (
                  <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_SUBPROJECT_COL_CLASS}`}>{t("lblSubProject", { ns: NS })}</th>
                ) : null}
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_TYPE_COL_CLASS}`}>{t("lblEquipmentType", { ns: NS, defaultValue: "Type équipement" })}</th>
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_UNIT_TEXT_COL_CLASS}`}>{t("lblBillingUnit", { ns: NS, defaultValue: "Unité" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EQUIPMENT_COST_COL_CLASS}`}>{t("lblEquipmentCost", { ns: NS, defaultValue: "Coût" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EQUIPMENT_QTY_COL_CLASS}`}>{t("lblQuantity", { ns: NS, defaultValue: "Qté" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EQUIPMENT_AMOUNT_COL_CLASS}`}>{t("lblAmount", { ns: NS, defaultValue: "Montant" })}</th>
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EQUIPMENT_REMARK_COL_CLASS}`}>{t("lblRemark", { ns: NS, defaultValue: "Remarque" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={equipmentTableColCount} className="px-4 py-8 text-center text-sm text-gray-400">
                    {t("msgNoEquipmentLines", { ns: NS, defaultValue: "Aucun équipement pour cette période." })}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <EquipmentRowEditor
                    key={row.clientRowId ?? `eq-${idx}`}
                    index={idx}
                    formStateHandlers={formStateHandlers}
                    findProjectHandlers={findProjectHandlers}
                    equipmentTypeOptions={equipmentTypeOptions}
                    readonly={readonly}
                    showSubProjectColumn={showSubProjectColumn}
                    rowIncludeSubProject={getRowIncludeSubProject(idx)}
                    getProjectId={() => getProjectIdForRow(idx)}
                  />
                ))
              )}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50">
              <tr>
                <td colSpan={equipmentFooterFirstColSpan} className={`px-2 py-2.5 text-right font-semibold text-gray-700 ${U3129_EQUIPMENT_TOTAL_LABEL_COL_CLASS}`}>
                  {t("lblTotal", { ns: NS, defaultValue: "Total" })}
                </td>
                <td className="px-1 py-2.5 pr-4 text-right font-semibold text-gray-900 tabular-nums">{formatCurrency(totalAmount, { decimalDigits: 2 })}</td>
                <td className="px-2 py-2.5" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default U3129EquipmentTab;
